import React, { useState, useEffect, useRef } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Box, Typography, IconButton, Badge, Menu, MenuItem, Tooltip } from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { FaBell, FaUser } from 'react-icons/fa';
import warehouseImage from '../assets/warehouse.png';

// Importação das páginas para o dashboard
import Entrada from './entrada';
import Saida from './saida';
import Mercadorias from './mercadorias';
import Gestor from './gestor';

const WarehouseOp = () => {
    const navigate = useNavigate();
    const location = useLocation();
    
    // Define o estado inicial a partir do parâmetro recebido (caso exista)
    const initialOption = location.state?.selectedOption || '';
    const [isExpanded, setIsExpanded] = useState(false);
    const [selectedOption, setSelectedOption] = useState(initialOption);
    
    // Estados para notificações e menu de usuário
    const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
    
    // Exemplo de objeto de usuário – ajuste conforme sua implementação
    const [user, setUser] = useState({
        nome_completo: "João Silva",
        seccao: "Admin"
    });
    const userRef = useRef(user);
    
    const apiBaseURL = 'https://plasticemployers.duckdns.org';
    
    useEffect(() => {
        // Inicia a animação de expansão após 500ms
        const timer = setTimeout(() => setIsExpanded(true), 500);
        return () => clearTimeout(timer);
    }, []);
    
    // Busca as notificações ao montar o componente
    useEffect(() => {
        const fetchNotifications = async () => {
            const token = localStorage.getItem('access');
            try {
                const response = await axios.get(`${apiBaseURL}/api/notifications/list/`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                const fetchedNotifications = Array.isArray(response.data)
                    ? response.data
                    : (response.data.results || []);
                setNotifications(fetchedNotifications);
                const unreadCount = fetchedNotifications.filter(n => !n.is_read).length;
                setUnreadNotificationsCount(unreadCount);
            } catch (error) {
                console.error('Erro ao buscar notificações:', error);
            }
        };
        fetchNotifications();
    }, []);
    
    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };
    
    const handleMenuClose = () => {
        setAnchorEl(null);
    };
    
    const handleNotificationMenuOpen = (event) => {
        setNotificationAnchorEl(event.currentTarget);
    };
    
    const handleNotificationMenuClose = () => {
        setNotificationAnchorEl(null);
    };
    
    const handleNotificationClick = async (notification) => {
        const token = localStorage.getItem('access');
        try {
            await axios.post(`${apiBaseURL}/api/notifications/mark-as-read/${notification.id}/`, {}, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
    
            setNotifications(prevNotifications => 
                prevNotifications.map(n => 
                    n.id === notification.id ? { ...n, is_read: true } : n
                )
            );
    
            setUnreadNotificationsCount(prevCount => prevCount > 0 ? prevCount - 1 : 0);
    
            if (userRef.current.seccao === 'Admin' || userRef.current.seccao === 'Escritorio') {
                navigate('/escritorio');
            } else {
                navigate('/marcar-ferias');
            }
        } catch (error) {
            console.error('Erro ao marcar a notificação como lida:', error);
        }
        handleNotificationMenuClose();
    };
    
    const handleLogout = () => {
        localStorage.removeItem('access');
        localStorage.removeItem('refresh');
        navigate('/');
    };
    
    // Título do card conforme a opção selecionada
    const getMenuTitle = () => {
        switch(selectedOption) {
            case 'entrada':
                return 'Entrada Menu';
            case 'saida':
                return 'Saída Menu';
            case 'mercadorias':
                return 'Mercadorias';
            case 'gestor':
                return 'Gestor Menu';
            default:
                return 'Warehouse Dashboard';
        }
    };
    
    // Renderiza o conteúdo principal com base na opção selecionada
    const renderContent = () => {
        switch(selectedOption) {
            case 'entrada':
                return <Entrada />;
            case 'saida':
                return <Saida />;
            case 'mercadorias':
                return <Mercadorias />;
            case 'gestor':
                return <Gestor />;
            default:
                return (
                    <Typography variant="body1" align="center">
                        Selecione uma opção no menu acima para visualizar o conteúdo.
                    </Typography>
                );
        }
    };
    
    // Mini menu de navegação – posicionado no canto superior esquerdo
    const miniNav = (
        <Box
            sx={{
                position: 'absolute',
                top: '0.5rem',
                left: '1rem',
                display: 'flex',
                gap: '0.5rem',
                zIndex: 2,
            }}
        >
            <Button 
                icon="pi pi-sign-in" 
                className="p-button-text p-button-lg"
                style={{ color: '#fff' }}
                onClick={() => setSelectedOption('entrada')}
            />
            <Button 
                icon="pi pi-sign-out" 
                className="p-button-text p-button-lg"
                style={{ color: '#fff' }}
                onClick={() => setSelectedOption('saida')}
            />
            <Button 
                icon="pi pi-box" 
                className="p-button-text p-button-lg"
                style={{ color: '#fff' }}
                onClick={() => setSelectedOption('mercadorias')}
            />
            <Button 
                icon="pi pi-user" 
                className="p-button-text p-button-lg"
                style={{ color: '#fff' }}
                onClick={() => setSelectedOption('gestor')}
            />
        </Box>
    );
    
    // Box de notificações e menu do usuário – posicionado no canto superior direito
    const notificationBox = (
        <Box
            sx={{
                position: 'absolute',
                top: '0.5rem',
                right: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                zIndex: 2,
            }}
        >
            <IconButton sx={{ color: 'white' }} onClick={handleNotificationMenuOpen}>
                <Badge badgeContent={unreadNotificationsCount} color="error">
                    <FaBell style={{ fontSize: 24 }} />
                </Badge>
            </IconButton>
            <Menu
                anchorEl={notificationAnchorEl}
                open={Boolean(notificationAnchorEl)}
                onClose={handleNotificationMenuClose}
                PaperProps={{
                    style: {
                        width: '280px',
                    },
                }}
            >
                {notifications.length === 0 ? (
                    <MenuItem onClick={handleNotificationMenuClose}>
                        Nenhuma nova notificação
                    </MenuItem>
                ) : (
                    notifications.map((notification, index) => (
                        <Tooltip key={index} title={notification.message} arrow>
                            <MenuItem 
                                onClick={() => handleNotificationClick(notification)} 
                                sx={{ 
                                    whiteSpace: 'normal', 
                                    wordWrap: 'break-word', 
                                    backgroundColor: notification.is_read ? 'white' : '#f0f0f0',
                                    fontWeight: notification.is_read ? 'normal' : 'bold'
                                }}
                            >
                                {notification.message}
                            </MenuItem>
                        </Tooltip>
                    ))
                )}
            </Menu>
            <IconButton onClick={handleMenuOpen} sx={{ color: 'white' }}>
                <FaUser style={{ fontSize: 24 }} />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                    style: {
                        width: '180px',
                    },
                }}
            >
                <MenuItem onClick={() => navigate('/account-settings', { state: { user } })}>
                    Definições de conta
                </MenuItem>
                <MenuItem onClick={() => navigate('/marcar-ferias', { state: { user } })}>
                    Marcar Férias
                </MenuItem>
                <MenuItem onClick={() => navigate('/faltas')}>Faltas</MenuItem>
                <MenuItem onClick={handleLogout}>Terminar Sessão</MenuItem>
            </Menu>
        </Box>
    );
    
    return (
        <Box
            sx={{
                minHeight: '100vh',
                backgroundImage: `url(${warehouseImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                position: 'relative',
                p: 2,
            }}
        >
            {miniNav}
            {notificationBox}
            <motion.div layoutId="sharedCard">
                <Card
                    style={{
                        transition: 'all 0.8s ease',
                        width: isExpanded ? '90%' : '40%',
                        minWidth: isExpanded ? '300px' : '280px',
                        margin: isExpanded ? '2rem auto' : 'auto',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                        padding: '2rem',
                    }}
                >
                    {/* Área de conteúdo dinâmica */}
                    <Box sx={{ mt: 3 }}>
                        {renderContent()}
                    </Box>
                </Card>
            </motion.div>
        </Box>
    );
};

export default WarehouseOp;
